import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import GovConsole from "@/webapp/government/GovConsole";
import SiteOwnerConsole from "@/webapp/site-owners/SiteOwnerConsole";
import FleetOwnerConsole from "@/webapp/fleet-owners/FleetOwnerConsole";
import {
  LogOut, LayoutDashboard, Truck, Users, ShieldCheck, Building2, MapPin,
  Clock, Wallet, BarChart3, CalendarCheck, BadgeCheck, AlertTriangle, Settings,
} from "lucide-react";

const ROLE_LABEL = {
  driver: "Driver",
  fleet_manager: "Fleet Manager",
  fleet_owner: "Fleet Owner",
  site_operator: "Site Operator",
  staff: "Staff",
  admin: "Administrator",
  gov_agency: "Government / Public Agency",
  partner: "Partner",
  supplier: "Supplier",
  finance: "Finance",
};

const ROLE_CARDS = {
  driver: [
    { icon: CalendarCheck, title: "Next Booking", value: "None yet", hint: "Book a secure pod on your route" },
    { icon: Clock, title: "Hours of Service", value: "11h 00m", hint: "Drive time remaining today" },
    { icon: Wallet, title: "Wallet", value: "$0.00", hint: "Add a payment method" },
    { icon: MapPin, title: "Nearby Hubs", value: "3 within 80km", hint: "Verified rest along the corridor" },
  ],
  fleet_manager: [
    { icon: Truck, title: "Active Drivers", value: "0", hint: "Drivers currently on shift" },
    { icon: ShieldCheck, title: "Compliance", value: "100%", hint: "HOS compliance this week" },
    { icon: CalendarCheck, title: "Bookings", value: "0", hint: "Upcoming pod & bay bookings" },
    { icon: Wallet, title: "Spend (MTD)", value: "$0.00", hint: "Fleet rest spend this month" },
  ],
  fleet_owner: [
    { icon: Users, title: "Fleet Size", value: "0", hint: "Drivers under this account" },
    { icon: BarChart3, title: "Utilisation", value: "—", hint: "Pod utilisation trend" },
    { icon: Wallet, title: "Billing", value: "$0.00", hint: "Current billing cycle" },
    { icon: ShieldCheck, title: "Duty of Care", value: "Active", hint: "Safety & compliance posture" },
  ],
  site_operator: [
    { icon: Building2, title: "Occupancy", value: "0 / 0", hint: "Bays & pods in use" },
    { icon: MapPin, title: "Gate Activity", value: "0", hint: "Check-ins today" },
    { icon: CalendarCheck, title: "Reservations", value: "0", hint: "Upcoming arrivals" },
    { icon: ShieldCheck, title: "Site Status", value: "Open", hint: "Capacity & availability" },
  ],
  admin: [
    { icon: Users, title: "Users", value: "—", hint: "Platform accounts" },
    { icon: Building2, title: "Sites", value: "—", hint: "Hubs on the network" },
    { icon: BarChart3, title: "Activity", value: "—", hint: "Platform-wide activity" },
    { icon: Settings, title: "System", value: "Healthy", hint: "Services & integrations" },
  ],
  gov_agency: [
    { icon: MapPin, title: "Corridor Coverage", value: "—", hint: "Hubs by jurisdiction" },
    { icon: ShieldCheck, title: "Safety Index", value: "—", hint: "Roadside incident reduction" },
    { icon: BarChart3, title: "Utilisation", value: "—", hint: "Regional usage reporting" },
    { icon: AlertTriangle, title: "Impound Zones", value: "Restricted", hint: "Access-controlled reporting" },
  ],
};

const Card = ({ icon: Icon, title, value, hint }) => (
  <div
    data-testid={`dash-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
    className="rounded-2xl bg-white p-5 shadow-[0_10px_28px_-14px_rgba(10,35,74,0.22),0_0_0_1px_rgba(10,35,74,0.05)] transition-transform duration-300 hover:-translate-y-0.5"
  >
    <div className="flex items-center justify-between">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#FF5722]/10">
        <Icon className="h-5 w-5 text-[#FF5722]" />
      </span>
    </div>
    <p className="mt-4 text-2xl font-bold text-[#0A234A] font-display">{value}</p>
    <p className="mt-1 text-sm font-semibold text-[#0A234A]">{title}</p>
    <p className="mt-0.5 text-xs text-neutral-500">{hint}</p>
  </div>
);

export default function Dashboard({ forceGov = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const role = user?.role || "driver";
  const cards = ROLE_CARDS[role] || ROLE_CARDS.driver;
  const isGov = role === "gov_agency";
  const govVerified = user?.gov_status === "verified";
  const firstName = (user?.name || "there").split(" ")[0];

  const signOut = async () => {
    navigate("/", { replace: true });
    await logout();
  };

  if ((isGov && govVerified) || forceGov) return <GovConsole />;

  if (role === "site_operator") return <SiteOwnerConsole />;
  if (role === "fleet_owner" || role === "fleet_manager") return <FleetOwnerConsole />;

  return (
    <div data-testid="dashboard-page" className="min-h-[100dvh] bg-[#F7F8FA] text-[#0A234A]">
      <header className="sticky top-0 z-40 border-b border-black/5 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 lg:px-10">
          <div className="flex items-center gap-3">
            <img src="/fleetpods_logo.png" alt="FleetPods" className="h-7 w-auto" />
            <span className="hidden items-center gap-1.5 rounded-full bg-[#0A234A]/5 px-3 py-1 text-xs font-bold text-[#0A234A] sm:flex">
              <LayoutDashboard className="h-3.5 w-3.5" /> Console
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right leading-tight">
              <p className="text-sm font-bold text-[#0A234A]" data-testid="dash-user-name">{user?.name || "Signed in"}</p>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#FF5722]" data-testid="dash-user-role">{ROLE_LABEL[role] || role}</p>
            </div>
            <button
              data-testid="dash-signout-btn"
              onClick={signOut}
              className="flex items-center gap-2 rounded-xl bg-[#0A234A] px-4 py-2 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#0d2c5e]"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 py-10 lg:px-10">
        {isGov && !govVerified && (
          <div data-testid="dash-gov-banner" className="mb-6 flex items-start gap-3 rounded-2xl border border-[#FF5722]/30 bg-[#FF5722]/5 p-4">
            <BadgeCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#FF5722]" />
            <div>
              <p className="text-sm font-bold text-[#0A234A]">Agency verification pending</p>
              <p className="text-sm text-neutral-600">Your government account is under review. Full corridor reporting unlocks once an administrator confirms your agency.</p>
            </div>
          </div>
        )}
        {isGov && govVerified && (
          <div data-testid="dash-gov-verified" className="mb-6 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <BadgeCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
            <div>
              <p className="text-sm font-bold text-[#0A234A]">Agency verified</p>
              <p className="text-sm text-neutral-600">Your government account is verified. Corridor safety and utilisation reporting is unlocked.</p>
            </div>
          </div>
        )}

        <div className="mb-8">
          <p className="text-sm font-bold uppercase tracking-[0.15em] text-[#FF5722]">{ROLE_LABEL[role] || role}</p>
          <h1 className="font-display mt-2 text-3xl font-bold tracking-tight text-[#0A234A] sm:text-4xl" data-testid="dash-welcome">
            Welcome back, {firstName}.
          </h1>
          <p className="mt-2 max-w-xl text-neutral-500">Here's your FleetPods overview. Live bookings, compliance and utilisation widgets are rolling out corridor by corridor.</p>
          {role === "admin" && (
            <button data-testid="dash-open-gov" onClick={() => navigate("/gov")} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#FF5722] px-4 py-2.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-[#F4511E]">
              <ShieldCheck className="h-4 w-4" /> Open Government Portal
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((c) => <Card key={c.title} {...c} />)}
        </div>
      </main>
    </div>
  );
}
